import { useEffect, useState, type ReactElement } from 'react';
import { trackFailed } from '../../../../../shared-web/track';
import { ApiError } from '../../auth/api-client';
import { emberApi, isAuthRequired, type ReadNote, type ReadPageResponse } from '../api';
import { provenanceLine, splitParagraphs } from '../format';
import { navigate } from '../router';
import { useRequireAuth } from '../use-require-auth';
import { EmberPhotoRow, photosUnder } from '../components/EmberPhotos';
import { EmberMediaHeader } from '../components/EmberMediaHeader';
import { PageBack } from '../components/PageBack';
import { PageHome } from '../components/PageHome';
import { PageFailure } from '../components/PageFailure';
import { PageLoading } from '../components/PageLoading';
import { NoteComposer, NoteThread, ThankYouButton } from '../components/ReadNotes';
import { notifyKnocksChanged } from '../components/use-knocks';
import './HandoffPage.css';
import './ReadPage.css';

/**
 * The read page `/read/:readId` (v4.0 §4.9 rewritten, 2026-09-27 拍板): an
 * Ember someone let this person read, because Ember thought where they stood
 * could help where this person stands. The whole Ember on paper — cover,
 * receipt, title, the text with its photos, the colophon — with, above it,
 * what Ember saw in it, and under it the notes: the two sides leave each
 * other sticky notes, one reader and one teller, both anonymous. The
 * reader's one tap is a thank-you.
 *
 * Opening it is the read (the server stamps opened_at); showing it is the
 * look at the teller's notes (seen). Theirs only: a 404 to anyone else. A
 * read the teller closed reads as exactly that.
 */
export function ReadPage({ readId, path }: { readId: string; path: string }): ReactElement {
  const status = useRequireAuth(path);
  const [data, setData] = useState<ReadPageResponse | null>(null);
  const [failed, setFailed] = useState<false | 'closed' | 'not_found' | 'load_failed'>(false);

  useEffect(() => {
    if (status !== 'authenticated') return undefined;
    let alive = true;
    setFailed(false);
    emberApi.read(readId)
      .then((r) => {
        if (!alive) return;
        setData(r);
        // The teller's notes shown are looked at: the reader's letter ends.
        if (r.notes.some((n) => n.by === 'owner' && !n.seen)) {
          emberApi.markReadSeen(readId).then(() => notifyKnocksChanged()).catch(() => { /* next time */ });
        }
      })
      .catch((err: unknown) => {
        if (!alive) return;
        if (isAuthRequired(err)) { navigate(`/auth?returnTo=${encodeURIComponent(path)}`, { replace: true }); return; }
        const s = err instanceof ApiError ? err.status : 0;
        if (s === 410) setFailed('closed');
        else if (s === 404) { setFailed('not_found'); trackFailed('read_load', 'not_found'); }
        else { setFailed('load_failed'); trackFailed('read_load', 'load_failed'); }
      });
    return () => { alive = false; };
  }, [readId, status, path]);

  const toolbar = (
    <div className="read-toolbar">
      <PageBack to="/talk?mode=chat" className="read-back" fallbackLabel="Back" />
      <div className="ember-toolbar-actions"><PageHome /></div>
    </div>
  );

  if (status !== 'authenticated') return <div className="ember-page-quiet">One moment…</div>;
  if (failed === 'closed') {
    return (
      <main className="ember-container ember-reading handoff-page read-page">
        {toolbar}
        <div className="handoff-gone">
          <h1>The person who told it closed it.</h1>
          <p>Nothing of yours changed.</p>
        </div>
      </main>
    );
  }
  if (failed === 'not_found') return <PageFailure title="This isn’t here." detail="It may have been given to someone else, or the link is old." logState="not_found" />;
  if (failed) return <PageFailure title="Couldn’t open this Ember" detail="Refresh to try again." logState="load_failed" />;
  if (!data) return <PageLoading />;

  const { ember, provenance, value, notes } = data;
  const receipt = provenanceLine(provenance ?? ember.provenance);
  const paragraphs = splitParagraphs(ember.canonical_text);
  const addNote = (note: ReadNote): void => setData((d) => (d ? { ...d, notes: [...d.notes, note] } : d));

  return (
    <main className="ember-container ember-reading handoff-page read-page">
      {toolbar}
      <EmberMediaHeader
        media={ember.media}
        alt={`${ember.title || 'Untitled Ember'} — illustration`}
        logEmberId={ember.id}
      />
      <header className="handoff-head">
        <p className="read-kicker">An Ember someone let you read</p>
        {receipt && <p className="handoff-receipt">{receipt}</p>}
        <h1 className="handoff-title" dir="auto">{ember.title || 'Untitled Ember'}</h1>
        {value && <p className="read-value" dir="auto"><span className="read-value-label">What Ember saw in it:</span> {value}</p>}
      </header>
      <article className="handoff-text">
        {paragraphs.map((p, i) => (
          <div className="handoff-para-block" key={i}>
            <p className="handoff-para" dir="auto">{p.text}</p>
            <EmberPhotoRow photos={photosUnder(ember.photos, p)} all={ember.photos ?? []} emberId={ember.id} />
          </div>
        ))}
      </article>
      <p className="handoff-colophon">
        <span className="handoff-seal" aria-hidden><span>told<br />here</span></span>
        <span>Told to Ember by a real person.</span>
      </p>

      {/* The notes: one reader, one teller, both anonymous. The thank-you is
          the one tap; a line of the reader's own is offered under it. */}
      <section className="read-notes-section" aria-labelledby="read-notes-title">
        <h2 id="read-notes-title" className="read-notes-title">Notes</h2>
        <p className="read-notes-lede">
          They said yes to someone they’ll never meet, because they thought where they stood might help where you stand. Anything you write goes to them, and to no one else. They see that someone read it — your avatar, never your name.
        </p>
        <NoteThread notes={notes} me="reader" />
        <ThankYouButton readId={readId} notes={notes} emberId={ember.id} onNote={addNote} />
        <NoteComposer readId={readId} emberId={ember.id} logState="reader" placeholder="A note for them" onNote={addNote} />
      </section>
    </main>
  );
}
